// Native-shell media URL shim.
//
// In the browser the SPA is served from the backend origin, so server-relative
// media paths ('/uploads/...') in <img>/<video>/<audio> just work. Inside a
// native shell (packaged Electron file://, Capacitor capacitor://localhost)
// the document origin is local — those same relative paths resolve against the
// WebView origin and 404, which is why member-list / People / Tracker avatars
// rendered broken on iOS while @os/messenger (which routes through mediaUrl())
// did not.
//
// Modules are browser-first and are CORRECT to emit relative paths; the shell
// is the special environment, so the shell-compat rewrite lives here in the
// host, at one seam, instead of chasing every `src={user.avatarUrl}` across
// @os/spaces, @os/people, @os/tracker (and every future module). Rewrites are
// one-way (relative → absolute), so the attribute-mutation observer cannot
// loop: after a rewrite the value no longer starts with '/uploads/'.
//
// Scope is deliberately narrow — only the media tags and only '/uploads/'
// paths. API calls already go through axios with an absolute baseURL, and CSS
// background images with upload paths don't occur in module code (audited
// 2026-06-10).
import { IS_NATIVE_SHELL, BACKEND_URL } from './config'

const UPLOADS_PREFIX = '/uploads/'
const MEDIA_TAGS = ['IMG', 'VIDEO', 'AUDIO', 'SOURCE']

function rewriteEl(el: Element): void {
  const src = el.getAttribute('src')
  if (src && src.startsWith(UPLOADS_PREFIX)) {
    el.setAttribute('src', `${BACKEND_URL}${src}`)
  }
}

function sweep(root: ParentNode): void {
  const sel = MEDIA_TAGS.map(t => `${t.toLowerCase()}[src^="${UPLOADS_PREFIX}"]`).join(', ')
  root.querySelectorAll(sel).forEach(rewriteEl)
}

// Install once from main.tsx, before the React root renders. No-op outside
// the native shell (BACKEND_URL is '' there and relative paths are correct).
// Returns a disposer so tests can disconnect the observer between cases —
// leaked subtree observers accumulate across a test file and ran the vitest
// worker out of memory before this was added.
export function installNativeShellMediaShim(): () => void {
  if (!IS_NATIVE_SHELL || !BACKEND_URL) return () => {}

  const observer = new MutationObserver(mutations => {
    for (const m of mutations) {
      if (m.type === 'attributes' && m.target instanceof Element) {
        rewriteEl(m.target)
      } else if (m.type === 'childList') {
        m.addedNodes.forEach(node => {
          if (!(node instanceof Element)) return
          if (MEDIA_TAGS.includes(node.tagName)) rewriteEl(node)
          sweep(node)
        })
      }
    }
  })

  observer.observe(document.documentElement, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['src'],
  })
  sweep(document)
  return () => observer.disconnect()
}
